/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v3";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  FirewallPolicy,
  FirewallPolicy$inboundSchema,
} from "./firewallpolicy.js";
import { LoggingPolicy, LoggingPolicy$inboundSchema } from "./loggingpolicy.js";
import { MatchExpr, MatchExpr$inboundSchema } from "./matchexpr.js";

/**
 * A stored gateway policy as returned by list endpoints. Exactly one of
 *
 * @remarks
 * `firewall`, or `logging` is set.
 */
export type PolicyResponse = {
  /**
   * Server-generated policy id. Regenerated on every `gateway.setPolicies` call, so treat it as stable only until the environment's policies are next replaced.
   */
  id: string;
  /**
   * Human-readable name shown in the dashboard.
   */
  name: string;
  /**
   * Disabled policies are stored but skipped during evaluation.
   */
  enabled: boolean;
  /**
   * Optional request matchers. The policy applies only to requests matching
   *
   * @remarks
   * all expressions; omitted when the policy applies to every request.
   */
  match?: Array<MatchExpr> | undefined;
  /**
   * Blocks matching requests.
   */
  firewall?: FirewallPolicy | undefined;
  /**
   * Adds request data to the log entries of matching requests. The gateway
   *
   * @remarks
   * always records a basic log entry for every request: method, host, path,
   * status, and latency. Each capture setting is a separate opt-in: request
   * headers, response headers, request body, response body, and query data.
   * The policy's `match` expressions select the requests. A policy without
   * `match` expressions matches every request. If more than one enabled
   * logging policy matches a request, the gateway combines their settings.
   * The gateway always redacts the `Authorization` header and configured key
   * locations before it stores headers or query data.
   */
  logging?: LoggingPolicy | undefined;
};

/** @internal */
export const PolicyResponse$inboundSchema: z.ZodType<
  PolicyResponse,
  z.ZodTypeDef,
  unknown
> = z.object({
  id: z.string(),
  name: z.string(),
  enabled: z.boolean(),
  match: z.array(MatchExpr$inboundSchema).optional(),
  firewall: FirewallPolicy$inboundSchema.optional(),
  logging: LoggingPolicy$inboundSchema.optional(),
});

export function policyResponseFromJSON(
  jsonString: string,
): SafeParseResult<PolicyResponse, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => PolicyResponse$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'PolicyResponse' from JSON`,
  );
}
